// src/services/staffConfirmation/staffConfirmationControls.ts - is "Confirm with
// Google" on for the signed-in operator?
//
// Two launch controls must both allow it, and both default OFF:
// - the api's security gate `admin.staff_confirmation_required`, read from the
//   launch-control list the Security gates panel reads, with the same reach rules
//   (securityGates.reachOf): ON for everyone, or ON for a cohort that names this
//   operator;
// - 88ID's tenant policy `staff.confirmation.enabled`. 88ID publishes no read of
//   it: while it is off every re-auth route answers 404. So the office learns it
//   from the first re-auth, and a 404 turns the confirmation off for the rest of
//   the session (noteIdentityConfirmationUnavailable).
// The office also needs to know where 88ID's staff tenant is
// (REACT_APP_IDENTITY_ISSUER, the variable the office sign-in work registers);
// without it the confirmation stays off.
//
// Anything unknown reads as OFF: the list not loaded yet, a failed read, the
// control missing or not marked as a security gate. OFF is today's code path,
// exactly, and the api stays the authority on what it accepts.
import { reachOf } from "../../console/pages/system/securityGates";
import type { LaunchControl, LaunchControlList } from "../launchControlService";
import { getQuery } from "../apiService";
import { beginGateRead, setGateOn } from "./staffConfirmationState";

export {
  identityStaffIssuer,
  isStaffConfirmationOn,
  noteIdentityConfirmationUnavailable,
  noteStaffSessionEnded,
  onStaffSessionEnded,
  resetStaffConfirmationControls,
  setStaffConfirmationGateForTests,
  useStaffConfirmationOn,
} from "./staffConfirmationState";

export const STAFF_CONFIRMATION_GATE_KEY = "admin.staff_confirmation_required";

const normalise = (id: string) => id.trim().toLowerCase();

/** Does this stored gate row cover this operator? */
export const gateCoversOperator = (
  control: LaunchControl | undefined,
  userId: string
): boolean => {
  if (!control || control.securityGate !== true) return false;
  const reach = reachOf(control);
  if (reach.kind === "everyone") return true;
  if (reach.kind === "cohort") return reach.userIds.includes(normalise(userId));
  return false;
};

type ReadControls = () => Promise<LaunchControl[]>;

const readFromApi: ReadControls = async () => {
  const response = await getQuery<{ data?: LaunchControlList }>(
    "/api/v2/admin/launch-controls"
  );
  return response.data?.data?.controls ?? [];
};

/**
 * Read the gate for the signed-in operator. Called once a session's profile is
 * known; a failed read leaves the confirmation OFF.
 */
export async function loadStaffConfirmationControls(
  userId: string | undefined,
  read: ReadControls = readFromApi
): Promise<void> {
  const stillLatest = beginGateRead();
  if (!userId) {
    setGateOn(false);
    return;
  }
  let gateOn = false;
  try {
    const controls = await read();
    gateOn = gateCoversOperator(
      controls.find((control) => control.key === STAFF_CONFIRMATION_GATE_KEY),
      userId
    );
  } catch {
    gateOn = false;
  }
  // A read that finished after sign-out (or a newer read) must not apply.
  if (stillLatest()) setGateOn(gateOn);
}
